import {type QueryHookOptions, useQuery} from 'services/apollo';
import {EzPage, EzHeading} from '@ezcater/recipe';
import gql from 'graphql-tag';
import {useTranslation} from 'react-i18next';
import DriverAssignments from 'services/graphql/DriverAssignments';
import {useFederationApiEppoFlag} from 'services/eppo';
import {
  type CompletedDeliveriesAssignmentsQuery,
  type CompletedDeliveriesAssignmentsQueryVariables,
  type DriverAssignmentsQuery,
  type DriverAssignmentsQueryVariables,
  DriverAssignmentState,
} from 'services/graphql/types';
import DeliveryCardContainer from 'components/DeliveryCardContainer';
import {addDays, startOfToday} from 'date-fns';
import {getAdaptedAssignmentsQuery} from 'services/driverApiAdapters';
import Layout from '../Layout';

export const AssignmentsQuery = gql(/* GraphQL */ `
  query CompletedDeliveriesAssignmentsQuery(
    $eventAtRange: TimeRangeInput
    $state: DriverAssignmentState
  ) {
    me {
      driverAccount {
        assignments(eventAtRange: $eventAtRange, state: $state) {
          nodes {
            dropoffAddress {
              city
              id
              state
              street1
              street2
              zip
            }
            eventAt
            formattedOrderNumber
            id
            pickupAddress {
              id
              timeZoneIdentifier
            }
            status
          }
        }
        id
      }
    }
  }
`);

type UseAssignmentsQueryOptions = Required<
  Pick<QueryHookOptions<DriverAssignmentsQuery, DriverAssignmentsQueryVariables>, 'variables'>
>;

type UseAssignmentsQueryResult = Pick<
  ReturnType<typeof useQuery<DriverAssignmentsQuery, DriverAssignmentsQueryVariables>>,
  'data' | 'error' | 'loading'
>;

const useAssignmentsQuery = (options: UseAssignmentsQueryOptions): UseAssignmentsQueryResult => {
  const featureFlag = useFederationApiEppoFlag();
  const newQuery = useQuery<
    CompletedDeliveriesAssignmentsQuery,
    CompletedDeliveriesAssignmentsQueryVariables
  >(AssignmentsQuery, {
    skip: !featureFlag,
    variables: {...options.variables, state: DriverAssignmentState.Completed},
  });
  const legacyQuery = useQuery<DriverAssignmentsQuery, DriverAssignmentsQueryVariables>(
    DriverAssignments,
    {skip: featureFlag, variables: options.variables}
  );

  if (featureFlag) {
    return {
      data: getAdaptedAssignmentsQuery(newQuery.data),
      error: newQuery.error,
      loading: newQuery.loading,
    };
  }

  return legacyQuery;
};

const CompletedDeliveries = () => {
  const {t} = useTranslation();
  const {loading, data} = useAssignmentsQuery({
    variables: {
      eventAtRange: {
        starting: addDays(startOfToday(), -30).toISOString(),
        ending: startOfToday().toISOString(),
      },
    },
  });

  const title = t('completedDeliveries.title');
  const header = t('completedDeliveries.header');
  const tableTitle = t('completedDeliveries.tableTitle');

  return (
    <Layout title={title}>
      <EzPage>
        <EzHeading size={'1'}>{header}</EzHeading>
        <DeliveryCardContainer data={data} loading={loading} header={tableTitle} />
      </EzPage>
    </Layout>
  );
};

export default CompletedDeliveries;
